import {
  buildRulesLessonItems,
  ruleLessonItemStepCount,
  type RuleLessonItem,
  type RuleZeptoRef,
} from "site/services/lesson-rule-slides";

export type LessonTimelineStepKey = "dictionaries" | "dialogs" | "groups" | "single" | "select" | "sentences" | "rules";

type TimelineStepDescriptor = {
  key: LessonTimelineStepKey;
  stages?: unknown[];
};

export type LessonTimelineRuleDraft = {
  kind: "rule";
  ref: RuleZeptoRef;
};

export type LessonTimelineExerciseDraft = {
  kind: "exercise";
  stepKey: LessonTimelineStepKey;
  subIndex: number;
};

export type LessonTimelineDraftEntry = LessonTimelineRuleDraft | LessonTimelineExerciseDraft;

export type LessonTimelineRuleEntry = {
  kind: "rule";
  item: RuleLessonItem;
};

export type LessonTimelineExerciseEntry = {
  kind: "exercise";
  stepKey: LessonTimelineStepKey;
  subIndex: number;
};

export type LessonTimelineEntry = LessonTimelineRuleEntry | LessonTimelineExerciseEntry;

export function finalizeLessonTimeline(
  draft: LessonTimelineDraftEntry[],
  catalog: Record<string, unknown> | null | undefined,
): LessonTimelineEntry[] {
  const timeline: LessonTimelineEntry[] = [];
  for (const entry of draft) {
    if (entry.kind === "exercise") {
      timeline.push(entry);
      continue;
    }
    const [item] = buildRulesLessonItems([entry.ref], catalog);
    if (item) {
      timeline.push({ kind: "rule", item });
    }
  }
  return timeline;
}

export function timelineEntryUnits(
  entry: LessonTimelineEntry,
  stepDescriptors: TimelineStepDescriptor[],
): number {
  if (entry.kind === "rule") {
    return ruleLessonItemStepCount(entry.item);
  }
  const descriptor = stepDescriptors.find((d) => d.key === entry.stepKey);
  return Math.max(1, descriptor?.stages?.length ?? 1);
}

export function getTimelineTotalUnits(
  timeline: LessonTimelineEntry[] | null | undefined,
  stepDescriptors: TimelineStepDescriptor[],
): number {
  if (!timeline?.length) {
    return 0;
  }
  let total = 0;
  for (const entry of timeline) {
    total += timelineEntryUnits(entry, stepDescriptors);
  }
  return total;
}

export function getTimelineCompletedUnits(
  timeline: LessonTimelineEntry[] | null | undefined,
  stepDescriptors: TimelineStepDescriptor[],
  timelineIndex: number,
  stageIndex: number,
  ruleStepIndex: number,
): number {
  if (!timeline?.length || timelineIndex < 0) {
    return 0;
  }

  let completed = 0;
  for (let i = 0; i < timelineIndex; i += 1) {
    completed += timelineEntryUnits(timeline[i]!, stepDescriptors);
  }

  const current = timeline[timelineIndex];
  if (!current) {
    return completed;
  }

  if (current.kind === "rule") {
    completed += Math.max(0, ruleStepIndex);
    return completed;
  }

  completed += Math.max(0, stageIndex);
  return completed;
}

export function findTimelineIndexByRuleId(
  timeline: LessonTimelineEntry[] | null | undefined,
  ruleZeptoId: number,
): number {
  if (!timeline?.length || !Number.isFinite(ruleZeptoId) || ruleZeptoId <= 0) {
    return -1;
  }
  for (let i = 0; i < timeline.length; i += 1) {
    const entry = timeline[i]!;
    if (entry.kind === "rule" && entry.item.id === ruleZeptoId) {
      return i;
    }
  }
  return -1;
}

function timelineEntryMatchesSaved(
  entry: LessonTimelineEntry,
  saved: { stepKey: string; subIndex: number; stepItemId?: number },
): boolean {
  if (saved.stepKey === "rules") {
    if (entry.kind !== "rule") {
      return false;
    }
    const ruleId = Number(saved.stepItemId);
    if (Number.isFinite(ruleId) && ruleId > 0) {
      return entry.item.id === ruleId;
    }
    return true;
  }
  return entry.kind === "exercise" && entry.stepKey === saved.stepKey && entry.subIndex === saved.subIndex;
}

export function resolveRestoreTimelineIndex(
  timeline: LessonTimelineEntry[] | null | undefined,
  saved: {
    stepKey: string;
    subIndex: number;
    stageIndex: number;
    ruleStepIndex?: number;
    timelineIndex?: number;
    stepItemId?: number;
  },
): number {
  if (!timeline?.length) {
    return -1;
  }

  const ruleStepIndex = Math.max(0, Math.floor(saved.ruleStepIndex ?? 0));

  if (typeof saved.timelineIndex === "number" && Number.isFinite(saved.timelineIndex)) {
    const idx = Math.floor(saved.timelineIndex);
    if (idx >= 0 && idx < timeline.length && timelineEntryMatchesSaved(timeline[idx]!, saved)) {
      return idx;
    }
  }

  if (saved.stepKey === "rules") {
    const ruleId = Number(saved.stepItemId);
    if (Number.isFinite(ruleId) && ruleId > 0) {
      const byId = findTimelineIndexByRuleId(timeline, ruleId);
      if (byId >= 0) {
        return byId;
      }
    }
  }

  return findTimelineIndexForPosition(
    timeline,
    saved.stepKey,
    saved.subIndex,
    saved.stageIndex,
    ruleStepIndex,
  );
}

export function findTimelineIndexForPosition(
  timeline: LessonTimelineEntry[] | null | undefined,
  stepKey: string,
  subIndex: number,
  stageIndex: number,
  ruleStepIndex: number,
): number {
  if (!timeline?.length) {
    return -1;
  }

  let bestIndex = -1;
  for (let i = 0; i < timeline.length; i += 1) {
    const entry = timeline[i]!;
    if (entry.kind === "rule") {
      if (stepKey === "rules" && subIndex === 0) {
        bestIndex = i;
        if (ruleStepIndex <= ruleLessonItemStepCount(entry.item) - 1) {
          return i;
        }
      }
      continue;
    }
    if (entry.stepKey === stepKey && entry.subIndex === subIndex) {
      bestIndex = i;
      return i;
    }
  }

  return bestIndex;
}
